// src/pages/Shared/Profile.jsx — works for buyer, seller, admin
import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, Box, Grid, TextField, Button,
  Avatar, Divider, CircularProgress, Alert, Chip,
} from '@mui/material';
import {
  Person, Edit, Save, Cancel, Email, Phone, Business,
  Public, Home, ContactPage, Language,
} from '@mui/icons-material';
import { authAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const ROLE_COLOR = { admin: 'error', seller: 'warning', buyer: 'success' };

const Profile = () => {
  const { refreshUser } = useAuth();
  const [profile,  setProfile]  = useState(null);
  const [editing,  setEditing]  = useState(false);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  const [formData, setFormData] = useState({
    company_name:   '',
    email:          '',
    phone:          '',
    country:        '',
    address:        '',
    contact_person: '',
    website:        '',
  });

  useEffect(() => { fetchProfile(); }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const res = await authAPI.verify();
      const u   = res?.data?.user || JSON.parse(localStorage.getItem('user') || '{}');
      applyProfile(u);
    } catch {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      applyProfile(u);
    } finally {
      setLoading(false);
    }
  };

  const applyProfile = (u) => {
    setProfile(u);
    setFormData({
      company_name:   u.company_name   || '',
      email:          u.email          || '',
      phone:          u.phone          || '',
      country:        u.country        || '',
      address:        u.address        || '',
      contact_person: u.contact_person || '',
      website:        u.website        || '',
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      // Use the full profile update endpoint (supports email, address, etc.)
      let res;
      try {
        res = await authAPI.updateFullProfile(formData);
        if (res?.data?.user) {
          applyProfile(res.data.user);
          // Update localStorage
          const stored = JSON.parse(localStorage.getItem('user') || '{}');
          localStorage.setItem('user', JSON.stringify({ ...stored, ...res.data.user }));
        }
      } catch (fullErr) {
        // Fallback to basic profile update
        await authAPI.updateProfile({
          company_name: formData.company_name,
          phone:        formData.phone,
          country:      formData.country,
        });
      }

      await refreshUser();
      await fetchProfile();
      setEditing(false);
      toast.success('Profile updated successfully!');
    } catch (err) {
      const msg = err?.data?.error || err?.response?.data?.message || err?.message || 'Failed to update profile';
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
      <CircularProgress size={48} />
    </Box>
  );

  const initials = profile?.company_name
    ? profile.company_name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
    : profile?.email?.[0]?.toUpperCase() || '?';

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header Card */}
      <Paper sx={{ p: 4, mb: 3, borderRadius: 3, background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)', color: '#fff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
          <Avatar
            src={profile?.avatar}
            sx={{ width: 80, height: 80, fontSize: '2rem', bgcolor: 'rgba(255,255,255,0.2)', border: '3px solid rgba(255,255,255,0.5)' }}>
            {initials}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>{profile?.company_name || 'User'}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>{profile?.email}</Typography>
            <Chip
              label={(profile?.user_type || 'user').toUpperCase()}
              size="small"
              sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700 }}
            />
          </Box>
          {!editing && (
            <Button variant="outlined" startIcon={<Edit />} onClick={() => setEditing(true)}
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}>
              Edit Profile
            </Button>
          )}
        </Box>
      </Paper>

      {/* Info / Edit Card */}
      <Paper sx={{ p: 4, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Person color="primary" /> Profile Information
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {editing ? (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Company / Full Name" value={formData.company_name}
                onChange={e => setFormData(p => ({ ...p, company_name: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Email Address" type="email" value={formData.email}
                onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                helperText="Changing email will update your login address" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Phone Number" value={formData.phone}
                onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))}
                placeholder="+1234567890" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Country" value={formData.country}
                onChange={e => setFormData(p => ({ ...p, country: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Address" value={formData.address} multiline rows={2}
                onChange={e => setFormData(p => ({ ...p, address: e.target.value }))}
                placeholder="Street address, city, state, ZIP" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Contact Person" value={formData.contact_person}
                onChange={e => setFormData(p => ({ ...p, contact_person: e.target.value }))}
                placeholder="Name of primary contact" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Website" value={formData.website}
                onChange={e => setFormData(p => ({ ...p, website: e.target.value }))}
                placeholder="https://yourcompany.com" />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button variant="outlined" startIcon={<Cancel />}
                  onClick={() => { setEditing(false); setError(''); }} disabled={saving}>
                  Cancel
                </Button>
                <Button variant="contained"
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
                  onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={3}>
            {[
              { icon: <Business color="primary" />, label: 'Company / Name',   value: profile?.company_name   || '—' },
              { icon: <Email    color="primary" />, label: 'Email Address',    value: profile?.email          || '—' },
              { icon: <Phone    color="primary" />, label: 'Phone Number',     value: profile?.phone          || '—' },
              { icon: <Public   color="primary" />, label: 'Country',          value: profile?.country        || '—' },
              { icon: <Home     color="primary" />, label: 'Address',          value: profile?.address        || '—' },
              { icon: <ContactPage color="primary" />, label: 'Contact Person',value: profile?.contact_person || '—' },
              { icon: <Language color="primary" />, label: 'Website',          value: profile?.website        || '—' },
            ].map(({ icon, label, value }) => (
              <Grid item xs={12} sm={6} key={label}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <Box sx={{ mt: 0.3 }}>{icon}</Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {label}
                    </Typography>
                    <Typography variant="body1" fontWeight={500} sx={{ wordBreak: 'break-word' }}>
                      {label === 'Website' && value !== '—'
                        ? <a href={value} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>{value}</a>
                        : value}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}

        <Divider sx={{ my: 3 }} />
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip label={`Role: ${(profile?.user_type || '—').toUpperCase()}`} color={ROLE_COLOR[profile?.user_type] || 'default'} />
          <Chip label={profile?.verified ? '✅ Verified' : '⏳ Pending Verification'} variant="outlined" />
          {profile?.oauth_provider && <Chip label={`Login: ${profile.oauth_provider}`} variant="outlined" />}
        </Box>
      </Paper>
    </Container>
  );
};

export default Profile;
